/* SIP Calculator — Interactive Math, URL Sync, Charts & CSV Export */

let donutChart = null;
let growthChart = null;

function getSipInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    investment: getVal('sipInvestment', 3000),
    interest: getVal('sipRate', 12),
    years: getVal('sipYears', 3),
    stepUp: getVal('sipStepUp', 0),
  };
}

function updateSipUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('investment', params.investment);
    url.searchParams.set('interest', params.interest);
    url.searchParams.set('years', params.years);
    if (params.stepUp > 0) {
      url.searchParams.set('stepUp', params.stepUp);
    } else {
      url.searchParams.delete('stepUp');
    }
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const investment = searchParams.get('investment');
  const interest = searchParams.get('interest');
  const years = searchParams.get('years');
  const stepUp = searchParams.get('stepUp');

  return {
    investment: investment !== null ? +investment : null,
    interest: interest !== null ? +interest : null,
    years: years !== null ? +years : null,
    stepUp: stepUp !== null ? +stepUp : null,
  };
}

function calculateSip(params) {
  const { investment, interest, years, stepUp } = params;
  const totalMonths = years * 12;
  const monthlyRate = interest / 100 / 12;

  let totalInvested = 0;
  let currentPortfolio = 0;
  const schedule = [];

  let currentMonthlyPmt = investment;

  for (let month = 1; month <= totalMonths; month++) {
    if (month > 1 && (month - 1) % 12 === 0 && stepUp > 0) {
      currentMonthlyPmt *= 1 + stepUp / 100;
    }

    totalInvested += currentMonthlyPmt;
    currentPortfolio = (currentPortfolio + currentMonthlyPmt) * (1 + monthlyRate);

    if (month % 12 === 0) {
      const year = month / 12;
      const estReturns = Math.max(0, currentPortfolio - totalInvested);
      schedule.push({
        year,
        monthlyContribution: currentMonthlyPmt,
        totalInvested: Math.round(totalInvested),
        estimatedReturns: Math.round(estReturns),
        totalValue: Math.round(currentPortfolio),
      });
    }
  }

  const roundedInvested = Math.round(totalInvested);
  const finalValue = Math.round(currentPortfolio);
  const roundedReturns = Math.max(0, finalValue - roundedInvested);

  return {
    totalInvested: roundedInvested,
    estimatedReturns: roundedReturns,
    totalValue: finalValue,
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('sipInvestmentDisplay', fmt(params.investment));
  setTxt('sipRateDisplay', fmtPct(params.interest));
  setTxt('sipYearsDisplay', `${params.years} yrs`);
  setTxt('sipStepUpDisplay', `${params.stepUp}%/yr`);
}

function renderSipResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('totalInvested', fmt(result.totalInvested));
  setTxt('estimatedReturns', fmt(result.estimatedReturns));
  setTxt('totalValue', fmt(result.totalValue));

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.totalInvested, result.estimatedReturns);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('SIP Chart rendering error:', err);
  }
}

function renderDonutChart(invested, returns) {
  const ctx = document.getElementById('sipDonutChart');
  if (!ctx) return;

  if (donutChart) donutChart.destroy();

  donutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Invested Amount', 'Estimated Returns'],
      datasets: [
        {
          data: [invested, returns],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent')],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('sipGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const investedData = schedule.map((r) => r.totalInvested);
  const valueData = schedule.map((r) => r.totalValue);

  if (growthChart) growthChart.destroy();

  growthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Total Value',
          data: valueData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderWidth: 2.5,
        },
        {
          label: 'Invested Amount',
          data: investedData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick'), maxTicksLimit: 10 },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('sipScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>Year ${row.year}</td>
      <td>${fmt(row.monthlyContribution)}</td>
      <td>${fmt(row.totalInvested)}</td>
      <td class="advantage-renter">+${fmt(row.estimatedReturns)}</td>
      <td class="advantage-buyer">${fmt(row.totalValue)}</td>
    </tr>
  `).join('');
}

function exportSipCsv() {
  const params = getSipInputs();
  const result = calculateSip(params);
  const headers = ['Year', 'Monthly Contribution', 'Total Invested', 'Estimated Returns', 'Total Portfolio Value'];
  const rows = result.schedule.map((r) => [
    r.year, Math.round(r.monthlyContribution), r.totalInvested, r.estimatedReturns, r.totalValue
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `sip-investment-schedule-${params.years}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applySipCountryRanges(country, overrideParams = {}) {
  if (!country || !country.sipDefaults || !country.sipRanges) return;
  const { sipDefaults, sipRanges } = country;

  const invSlider = document.getElementById('sipInvestment');
  if (invSlider) {
    invSlider.min = sipRanges.investment.min;
    invSlider.max = sipRanges.investment.max;
    invSlider.step = sipRanges.investment.step;
    invSlider.value = overrideParams.investment ?? sipDefaults.investment;
  }

  const rateSlider = document.getElementById('sipRate');
  if (rateSlider) {
    rateSlider.min = sipRanges.interest.min;
    rateSlider.max = sipRanges.interest.max;
    rateSlider.step = sipRanges.interest.step;
    rateSlider.value = overrideParams.interest ?? sipDefaults.interest;
  }

  const yearsSlider = document.getElementById('sipYears');
  if (yearsSlider) {
    yearsSlider.min = sipRanges.years.min;
    yearsSlider.max = sipRanges.years.max;
    yearsSlider.step = sipRanges.years.step;
    yearsSlider.value = overrideParams.years ?? sipDefaults.years;
  }
}

function recalculateSip() {
  const params = getSipInputs();
  updateDisplays(params);
  updateSipUrlParams(params);
  const result = calculateSip(params);
  renderSipResults(result, params);
}

function initSipCalculator() {
  if (!document.getElementById('sipInvestment')) return; // Exit if not on SIP page

  const urlParams = parseUrlParams();

  initCountry((country) => {
    applySipCountryRanges(country, urlParams);
    recalculateSip();
  });

  if (typeof currentCountry !== 'undefined') {
    applySipCountryRanges(currentCountry, urlParams);
  }

  if (urlParams.investment !== null && document.getElementById('sipInvestment')) {
    document.getElementById('sipInvestment').value = urlParams.investment;
  }
  if (urlParams.interest !== null && document.getElementById('sipRate')) {
    document.getElementById('sipRate').value = urlParams.interest;
  }
  if (urlParams.years !== null && document.getElementById('sipYears')) {
    document.getElementById('sipYears').value = urlParams.years;
  }
  if (urlParams.stepUp !== null && document.getElementById('sipStepUp')) {
    document.getElementById('sipStepUp').value = urlParams.stepUp;
  }

  const inputs = document.querySelectorAll('input[type="range"]');
  inputs.forEach((el) => el.addEventListener('input', recalculateSip));

  const exportBtn = document.getElementById('exportSipCsv');
  if (exportBtn) exportBtn.addEventListener('click', exportSipCsv);

  recalculateSip();
}

document.addEventListener('DOMContentLoaded', initSipCalculator);
